/**
 * Modal de firma electrónica (21 CFR Part 11 §11.50).
 *
 * Comportamiento según FIRMA_ELECTRONICA_ENABLED:
 *   - true:  pide password + significado + razón (opcional u obligatoria).
 *   - false: pide solo razón (cuando requiereRazon=true) o solo confirma OK.
 *
 * El componente es controlado: el caller setea `open` y recibe el resultado
 * por `onResolve`. Patrón promise-resolver — el caller resuelve la promise.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSessionStore } from '@/stores/session';
import { useAuditStore } from '@/stores/audit';
import {
  FIRMA_ELECTRONICA_ENABLED,
  SIGNIFICADOS_FIRMA,
  type SignificadoFirma,
} from './firma-config';

export interface FirmaElectronicaResult {
  firmado: boolean;
  password?: string;
  significado?: SignificadoFirma;
  razon?: string;
}

export interface FirmaElectronicaModalProps {
  open: boolean;
  titulo: string;
  resumen: string;
  /** Si true, la razón es campo obligatorio. */
  requiereRazon?: boolean;
  /**
   * Entidad firmada (opcional). Si se pasa, la entry de audit
   * `firma_electronica` (R3) la incluye como `entidadTipo`/`entidadId` —
   * permite reportes de QA del tipo "todas las operaciones firmadas por X".
   */
  entidad?: { tipo: string; id: string };
  onResolve: (result: FirmaElectronicaResult) => void;
}

export function FirmaElectronicaModal({
  open,
  titulo,
  resumen,
  requiereRazon = false,
  entidad,
  onResolve,
}: FirmaElectronicaModalProps) {
  const usuario = useSessionStore((s) => s.usuario);
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  const [password, setPassword] = useState('');
  const [significado, setSignificado] = useState<SignificadoFirma>(SIGNIFICADOS_FIRMA[1]);
  const [razon, setRazon] = useState('');
  const [error, setError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setSignificado(SIGNIFICADOS_FIRMA[1]);
      setRazon('');
      setError(null);
      const t = setTimeout(() => firstInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleConfirmar = () => {
    if (FIRMA_ELECTRONICA_ENABLED && !password) {
      setError('La contraseña es obligatoria.');
      return;
    }
    if (requiereRazon && !razon.trim()) {
      setError('La razón es obligatoria para esta acción.');
      return;
    }
    // FIX R3 (GAMP 5 §11.50/§11.200): cada firma queda registrada como evento
    // auditable independiente, ADEMÁS de la entry de la mutación que dispara.
    // Permite reportes regulatorios del tipo "todas las firmas de X usuario"
    // sin tener que escanear las entries de cada acción.
    addAuditEntry({
      categoria: 'operacion_critica',
      accion: 'firma_electronica',
      entidadTipo: entidad?.tipo,
      entidadId: entidad?.id,
      significadoFirma: FIRMA_ELECTRONICA_ENABLED ? significado : 'Confirmación operativa (firma simplificada · flag off)',
      detalle: titulo,
      razon: razon.trim() || undefined,
    });
    onResolve({
      firmado: true,
      password: FIRMA_ELECTRONICA_ENABLED ? password : undefined,
      significado: FIRMA_ELECTRONICA_ENABLED ? significado : undefined,
      razon: razon.trim() || undefined,
    });
  };

  const handleCancelar = () => onResolve({ firmado: false });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleCancelar(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            {FIRMA_ELECTRONICA_ENABLED
              ? 'Firma electrónica conforme 21 CFR Part 11 / GAMP 5. Esta acción quedará registrada en el audit trail.'
              : 'Esta acción quedará registrada en el audit trail con tu usuario y timestamp.'}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded border-l-4 border-blue-400 bg-blue-50 p-3 text-xs text-blue-900">
          {resumen}
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Usuario</Label>
            <div className="mt-1 rounded bg-gray-100 px-3 py-2 font-mono text-sm">
              {usuario ? `${usuario.username} · ${usuario.nombre}` : '—'}
            </div>
          </div>

          {FIRMA_ELECTRONICA_ENABLED && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="firma-password">
                  Contraseña <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firma-password"
                  ref={firstInputRef}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Re-ingresá tu contraseña"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="firma-significado">
                  Significado de la firma <span className="text-red-500">*</span>
                </Label>
                <select
                  id="firma-significado"
                  value={significado}
                  onChange={(e) => setSignificado(e.target.value as SignificadoFirma)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                >
                  {SIGNIFICADOS_FIRMA.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="firma-razon">
              Razón / observaciones{' '}
              {requiereRazon ? (
                <span className="text-red-500">* (obligatoria)</span>
              ) : (
                <span className="text-gray-600">(opcional)</span>
              )}
            </Label>
            <textarea
              id="firma-razon"
              autoFocus={!FIRMA_ELECTRONICA_ENABLED}
              rows={2}
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder={
                requiereRazon
                  ? 'Justificá la modificación o cancelación...'
                  : 'Notas adicionales (opcional)'
              }
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancelar}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
          >
            {FIRMA_ELECTRONICA_ENABLED ? 'Firmar y confirmar' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
